import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams, useLocation } from 'react-router-dom';
import BaseInput from '../ui/BaseInput';
import { Button } from '../ui';
import { VerifyEmailFormData, VerifyEmailErrors } from '../../types/auth';
import { API_URLS } from '../../constants';
import { useAuth, useLanguage } from '@/contexts';

const VerifyNewEmailPage = () => {
  const navigate = useNavigate();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const { isLoggedIn, checkSession, user } = useAuth();

  const [formData, setFormData] = useState<VerifyEmailFormData>({
    email: searchParams.get('email') || '',
    code: '',
  });

  const [errors, setErrors] = useState<VerifyEmailErrors>({});
  const [loading, setLoading] = useState(false);
  const [resendLoading, setResendLoading] = useState(false);
  const [resendSuccess, setResendSuccess] = useState(false);
  const [resendCooldown, setResendCooldown] = useState(0);
  const [emailSent, setEmailSent] = useState(false);

  // Redirect SSO users back to settings
  useEffect(() => {
    if (user && (user.isSSOOnly || user.hasSSO)) {
      navigate('/settings');
    }
  }, [user, navigate]);

  // Handle emailSent state from navigation
  useEffect(() => {
    if (location.state?.emailSent) {
      setEmailSent(true);
      // Clear the state to prevent showing message on refresh
      window.history.replaceState({}, document.title);
    }
  }, [location.state]);

  // Redirect if not logged in
  useEffect(() => {
    if (!isLoggedIn) {
      navigate('/auth/login');
    }
  }, [isLoggedIn, navigate]);

  // Resend cooldown timer
  useEffect(() => {
    if (resendCooldown > 0) {
      const timer = setTimeout(() => setResendCooldown(resendCooldown - 1), 1000);
      return () => clearTimeout(timer);
    }
  }, [resendCooldown]);

  const handleChange = (field: keyof VerifyEmailFormData) => (value: string) => {
    setFormData(prev => ({ ...prev, [field]: value }));
    // Clear field-specific errors when user starts typing
    if (errors[field]) {
      setErrors(prev => ({ ...prev, [field]: undefined }));
    }
  };

  const validateForm = (): boolean => {
    const newErrors: VerifyEmailErrors = {};

    if (!formData.email.trim()) {
      newErrors.email = t('newEmailRequired');
    } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      newErrors.email = t('verificationEmailValidError');
    }

    if (!formData.code.trim()) {
      newErrors.code = t('verificationCodeRequired');
    } else if (!/^\d{6}$/.test(formData.code.trim())) {
      newErrors.code = t('verificationCodeInvalid');
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!validateForm()) {
      return;
    }

    setLoading(true);
    setErrors({});

    try {
      const response = await fetch(API_URLS.AUTH.VERIFY_NEW_EMAIL, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          newEmail: formData.email.trim(),
          code: formData.code.trim(),
        }),
      });

      const result = await response.json();

      if (response.ok) {
        // Update authentication state to reflect new email
        await checkSession();

        // Success: redirect to settings page with success message
        navigate('/settings', {
          state: {
            emailUpdateSuccess: true,
            newEmail: formData.email.trim()
          }
        });
      } else {
        if (response.status === 400 && result.fieldErrors) {
          setErrors(result.fieldErrors);
        } else {
          // Failure: redirect back to update email page with error
          navigate('/auth/update-email', {
            state: {
              error: 'New email not verified',
              previousEmail: formData.email.trim()
            }
          });
        }
      }
    } catch (error) {
      console.error('Email verification error:', error instanceof Error ? error.message : 'Unknown error');
      setErrors({
        general: 'Network error. Please check your connection and try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const handleResendCode = async () => {
    if (!formData.email.trim()) {
      setErrors({ email: t('verificationEmailError') });
      return;
    }

    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(formData.email)) {
      setErrors({ email: t('verificationEmailValidError') });
      return;
    }

    setResendLoading(true);
    setErrors({});
    setResendSuccess(false);

    try {
      const response = await fetch(API_URLS.AUTH.SEND_NEW_EMAIL_VERIFICATION, {
        method: 'POST',
        credentials: 'include',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          newEmail: formData.email.trim(),
        }),
      });

      const result = await response.json();

      if (response.ok) {
        setResendSuccess(true);
        setResendCooldown(60); // 60 second cooldown
        setFormData(prev => ({ ...prev, code: '' })); // Clear the code field
      } else {
        setErrors({
          general: result.message || t('resendVerificationCodeFailed')
        });
      }
    } catch (error) {
      console.error('Resend error:', error instanceof Error ? error.message : t('unknownError'));
      setErrors({
        general: t('networkError')
      });
    } finally {
      setResendLoading(false);
    }
  };

  // Don't render for SSO users
  if (user && (user.isSSOOnly || user.hasSSO)) {
    return null;
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="max-w-md w-full space-y-8">
        <div>
          <h2 className="mt-6 text-center text-3xl font-extrabold text-gray-900">
            {t('verifyYourNewEmail')}
          </h2>
          <p className="mt-2 text-center text-sm text-gray-600">
            {t('sentVerificationCode')}
            {formData.email && (
              <span className="block font-medium text-gray-900 mt-1">
                {formData.email}
              </span>
            )}
          </p>
        </div>

        <form className="mt-8 space-y-6" onSubmit={handleSubmit}>
          {emailSent && (
            <div className="rounded-lg bg-green-50 border border-green-200 p-4 shadow-sm">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-semibold text-green-800">
                    {t('verificationEmailSent')}
                  </h3>
                  <div className="mt-1 text-sm text-green-700">
                    <p>{t('verificationCodeSentToYourNewEmailAddress')}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {errors.general && (
            <div className="rounded-lg bg-red-50 border border-red-200 p-4 shadow-sm">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-red-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.28 7.22a.75.75 0 00-1.06 1.06L8.94 10l-1.72 1.72a.75.75 0 101.06 1.06L10 11.06l1.72 1.72a.75.75 0 101.06-1.06L11.06 10l1.72-1.72a.75.75 0 00-1.06-1.06L10 8.94 8.28 7.22z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-semibold text-red-800">
                    {t('verificationFailed')}
                  </h3>
                  <div className="mt-1 text-sm text-red-700">
                    <p>{errors.general}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {resendSuccess && (
            <div className="rounded-lg bg-green-50 border border-green-200 p-4 shadow-sm">
              <div className="flex items-start">
                <div className="flex-shrink-0">
                  <svg className="h-5 w-5 text-green-400" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                  </svg>
                </div>
                <div className="ml-3 flex-1">
                  <h3 className="text-sm font-semibold text-green-800">
                    {t('verificationCodeSentSuccessfullyNewEmail')}
                  </h3>
                  <div className="mt-1 text-sm text-green-700">
                    <p>{t('verificationCodeSentSuccessfullyCheckEmail')}</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="space-y-4">
            <BaseInput
              label={t('newEmailAddress')}
              name="email"
              type="email"
              value={formData.email}
              onChange={handleChange('email')}
              error={errors.email}
              required
              placeholder="your-new@email.com"
              autoComplete="email"
            />

            <BaseInput
              label={t('sixDigitVerificationCode')}
              name="code"
              type="text"
              value={formData.code}
              onChange={handleChange('code')}
              error={errors.code}
              required
              placeholder="123456"
              maxLength={6}
              pattern="\d{6}"
              autoComplete="one-time-code"
              helperText={t('sixDigitVerificationCodeHelperText')}
            />
          </div>

          <div className="space-y-4 pt-2">
            <Button
              type="submit"
              loading={loading}
              className="w-full py-3 bg-brand-dark-blue hover:bg-brand-electric-blue disabled:bg-gray-400 disabled:cursor-not-allowed text-white font-semibold rounded-lg transition-all duration-200 focus:outline-none focus:ring-2 focus:ring-brand-electric-blue focus:ring-offset-2 shadow-sm hover:shadow-md"
              disabled={loading}
            >
              {loading ? t('verifying') : t('verifyYourNewEmail')}
            </Button>

            <div className="text-center">
              <button
                type="button"
                onClick={handleResendCode}
                disabled={resendLoading || resendCooldown > 0}
                className={`text-sm font-medium transition-colors duration-200 px-3 py-2 rounded-md ${resendLoading || resendCooldown > 0
                    ? 'text-gray-400 cursor-not-allowed'
                    : 'text-brand-electric-blue hover:text-brand-dark-blue hover:bg-brand-light-gray cursor-pointer'
                  }`}
              >
                {resendLoading ? (
                  <span className="flex items-center justify-center space-x-2">
                    <div className="w-4 h-4 border-2 border-brand-electric-blue border-t-transparent rounded-full animate-spin" />
                    <span>{t('sending')}</span>
                  </span>
                ) : resendCooldown > 0 ? (
                  `${t('resendCodeIn')} ${resendCooldown}s`
                ) : (
                  t('resendVerificationCode')
                )}
              </button>
            </div>
          </div>

          <div className="text-center pt-2">
            <p className="text-sm text-gray-600">
              {t('wantToCancel')} {' '}
              <button
                type="button"
                onClick={() => navigate('/settings')}
                className="text-gray-600 hover:text-gray-800 font-medium transition-colors duration-150 px-2 py-1 rounded hover:bg-gray-50"
              >
                {t('backToSettings')}
              </button>
            </p>
          </div>
        </form>
      </div>
    </div>
  );
};

export default VerifyNewEmailPage;
